import React, { useMemo } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Box, Stack, Inline, Text, xcss } from '@atlaskit/primitives';
import { AiIcon } from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-icon/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { FireScreenAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { mainVirtualServiceAgentCtaCardQuery } from '@atlassian/jira-relay/src/__generated__/mainVirtualServiceAgentCtaCardQuery.graphql';
import { usePathParam } from '@atlassian/react-resource-router';

import messages from './messages.tsx';

export const VirtualServiceAgentCtaCard = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<mainVirtualServiceAgentCtaCardQuery>;
}) => {
	const { formatMessage } = useIntl();
	const [projectKey] = usePathParam('projectKey');
	const data = usePreloadedQuery<mainVirtualServiceAgentCtaCardQuery>(
		graphql`
			query mainVirtualServiceAgentCtaCardQuery(
				$cloudId: ID!
				$key: String!
				$checkForAiEnabledFF: Boolean!
			) @preloadable {
				jira {
					jiraProjectByKey(cloudId: $cloudId, key: $key) {
						knowledgeBaseArticlesCount(cloudId: $cloudId) @required(action: THROW) {
							... on KnowledgeBaseArticleCountSource {
								count @required(action: THROW)
							}
						}
						virtualAgentConfiguration {
							__typename
						}
						isVirtualAgentEnabled
					}
				}
				virtualAgent @optIn(to: ["VirtualAgent"]) {
					virtualAgentEntitlements(cloudId: $cloudId) {
						isVirtualAgentAvailable
						isAiEnabledInAdminHub @include(if: $checkForAiEnabledFF)
					}
				}
			}
		`,
		queryReference,
	);

	const numberOfArticles = data.jira?.jiraProjectByKey?.knowledgeBaseArticlesCount.count ?? 0;
	const vaFeaturesAreNotCorrectlyConfigured =
		data.virtualAgent?.virtualAgentEntitlements?.isVirtualAgentAvailable === false ||
		(data.virtualAgent?.virtualAgentEntitlements?.isAiEnabledInAdminHub === false &&
			fg('require_ai_enabled_for_jsm_virtual_agent'));

	const isVACreated =
		data.jira?.jiraProjectByKey?.virtualAgentConfiguration?.__typename ===
		'VirtualAgentConfiguration';
	const isVirtualAgentEnabled = data.jira?.jiraProjectByKey?.isVirtualAgentEnabled;

	const renderCta = useMemo(() => {
		if (isVirtualAgentEnabled) {
			return (
				<Link
					onClick={(_, analyticsEvent) => {
						// https://data-portal.internal.atlassian.com/analytics/registry/74456
						fireUIAnalytics(analyticsEvent, 'kbVirtualAgentTouchpointCta', {
							actionType: 'manage',
						});
					}}
					target="_blank"
					href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent`}
				>
					{formatMessage(messages.manageCta)}
				</Link>
			);
		}
		if (isVACreated) {
			return (
				<Link
					onClick={(_, analyticsEvent) => {
						// https://data-portal.internal.atlassian.com/analytics/registry/74456
						fireUIAnalytics(analyticsEvent, 'kbVirtualAgentTouchpointCta', {
							actionType: 'turnOnAiAnswers',
						});
					}}
					target="_blank"
					href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/ai-answers`}
				>
					{formatMessage(messages.turnOnAiAnswersCta)}
				</Link>
			);
		}
		return (
			<Link
				onClick={(_, analyticsEvent) => {
					// https://data-portal.internal.atlassian.com/analytics/registry/74456
					fireUIAnalytics(analyticsEvent, 'kbVirtualAgentTouchpointCta', {
						actionType: 'setupVSA',
					});
				}}
				target="_blank"
				href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent`}
			>
				{formatMessage(messages.turnOnVsaCta)}
			</Link>
		);
	}, [formatMessage, isVACreated, isVirtualAgentEnabled, projectKey]);

	if (numberOfArticles < 10 || vaFeaturesAreNotCorrectlyConfigured) {
		return null;
	}

	return (
		<Box padding="space.200" xcss={cardContainerStyles}>
			<Stack space="space.050">
				<Inline space="space.050" alignBlock="center">
					<AiIcon size="medium" label="" />
					<Heading size="xsmall">{formatMessage(messages.cardTitle)}</Heading>
					{isVirtualAgentEnabled ? (
						<Lozenge appearance="success">{formatMessage(messages.activeLabel)}</Lozenge>
					) : (
						<Lozenge>{formatMessage(messages.inactiveLabel)}</Lozenge>
					)}
				</Inline>
				{!isVirtualAgentEnabled && <Text>{formatMessage(messages.cardDescription)}</Text>}
				{renderCta}
			</Stack>
			{/* https://data-portal.internal.atlassian.com/analytics/registry/74455 */}
			<FireScreenAnalytics />
		</Box>
	);
};

const cardContainerStyles = xcss({
	minWidth: '300px',
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderWidth: 'border.width',
});
